import API from "../context/API";

/**
 * Centralized Analytics API Service
 * Handles all analytics data fetching with consistent error handling and retry logic
 */

// Base analytics API configuration
const ANALYTICS_ENDPOINTS = {
  OVERVIEW: '/analytics/overview',
  TRENDS: '/analytics/trends',
  CATEGORIES: '/analytics/categories',
  TOP_ITEMS: '/analytics/top-items',
  SALES: '/analytics/sales',
  ITEM_SALES: '/analytics/item-sales',
  CATEGORY_SALES: '/analytics/category-sales',
  PROFIT_MARGINS: '/analytics/profit-margins',
  DELIVERY_COST_ANALYSIS: '/analytics/delivery-cost-analysis',
  FEE_BREAKDOWN: '/analytics/fee-breakdown',
  MERCHANT_EARNINGS: '/merchant/{merchantId}/earnings',
  CUSTOMERS: '/analytics/customers',
  DISPUTES: '/analytics/disputes',
  POPULAR_ITEMS: '/analytics/popular-items',
  TRENDING_ITEMS: '/analytics/trending-items',
  ITEM_PERFORMANCE_COMPARISON: '/analytics/item-performance-comparison',
  ACTIVE_SESSIONS: '/analytics/active-sessions',
  DEVICES: '/analytics/devices',
  STORES: '/analytics/stores',
  MERCHANTS: '/analytics/merchants',
  STAFF: '/analytics/staff',
  MANAGERS: '/analytics/managers',
  ADMINS: '/analytics/admins'
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryMultiplier: 2 // Exponential backoff
};

/**
 * Generic API call with retry logic
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @param {object} options - Additional options
 * @returns {Promise} API response
 */
const apiCallWithRetry = async (endpoint, params = {}, options = {}) => {
  const { maxRetries, retryDelay, retryMultiplier } = RETRY_CONFIG;
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = localStorage.getItem("access_token");
      
      if (!token) {
        throw new Error("No access token found");
      }

      const response = await API.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...params,
          _t: Date.now(), // Cache busting
        },
        ...options
      });

      // Validate response structure
      if (!response.data) {
        throw new Error("Invalid response structure: missing data");
      }

      return response;
    } catch (error) {
      lastError = error;
      
      // Don't retry on authentication errors
      if (error.response?.status === 401) {
        throw error;
      }

      // Don't retry on client errors (4xx) except 408, 429
      if (error.response?.status >= 400 && error.response?.status < 500 && 
          error.response?.status !== 408 && error.response?.status !== 429) {
        throw error;
      }

      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying with exponential backoff
      const delay = retryDelay * Math.pow(retryMultiplier, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.warn(`API call failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
    }
  }

  throw lastError;
};

/**
 * Build common parameters for analytics requests
 * @param {object} options - Request options
 * @returns {object} Formatted parameters
 */
const buildAnalyticsParams = (options = {}) => {
  const {
    startDate,
    endDate,
    filter,
    merchantId,
    store,
    includeComparison = false,
    page = 1,
    limit = 10,
    sortBy,
    sortOrder = 'desc',
    search,
    ...otherParams
  } = options;

  const params = {};

  // Date range parameters
  if (startDate && endDate) {
    params.startDate = typeof startDate === 'string' ? startDate : startDate.format('YYYY-MM-DD');
    params.endDate = typeof endDate === 'string' ? endDate : endDate.format('YYYY-MM-DD');
  }

  // Filter parameters
  if (filter) {
    params.filter = filter;
  }

  // Entity parameters
  if (merchantId) {
    params.merchantId = merchantId;
  }
  if (store) {
    params.store = store;
  }

  // Comparison parameter
  if (includeComparison) {
    params.includeComparison = includeComparison;
  }

  // Pagination parameters
  if (page) {
    params.page = page;
  }
  if (limit) {
    params.limit = limit;
  }

  // Sorting parameters
  if (sortBy) {
    params.sortBy = sortBy;
    params.sortOrder = sortOrder;
  }

  // Search parameter
  if (search) {
    params.search = search;
  }

  // Add any other parameters
  Object.assign(params, otherParams);

  return params;
};

/**
 * Analytics API Service Class
 */
class AnalyticsAPI {
  /**
   * Fetch overview analytics data
   * @param {object} options - Request options
   * @returns {Promise} Overview data
   */
  static async getOverview(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.OVERVIEW, params);
    return response.data;
  }

  /**
   * Fetch trends analytics data
   * @param {object} options - Request options
   * @returns {Promise} Trends data
   */
  static async getTrends(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.TRENDS, params);
    return response.data;
  }

  /**
   * Fetch categories analytics data
   * @param {object} options - Request options
   * @returns {Promise} Categories data
   */
  static async getCategories(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.CATEGORIES, params);
    return response.data;
  }

  /**
   * Fetch top items analytics data
   * @param {object} options - Request options
   * @returns {Promise} Top items data
   */
  static async getTopItems(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.TOP_ITEMS, params);
    return response.data;
  }

  /**
   * Fetch sales analytics data
   * @param {object} options - Request options
   * @returns {Promise} Sales data
   */
  static async getSales(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.SALES, params);
    return response.data;
  }

  /**
   * Fetch item sales analytics data
   * @param {object} options - Request options
   * @returns {Promise} Item sales data
   */
  static async getItemSales(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ITEM_SALES, params);
    return response.data;
  }

  /**
   * Fetch category sales analytics data
   * @param {object} options - Request options
   * @returns {Promise} Category sales data
   */
  static async getCategorySales(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.CATEGORY_SALES, params);
    return response.data;
  }

  /**
   * Fetch profit margins analytics data
   * @param {object} options - Request options
   * @returns {Promise} Profit margins data
   */
  static async getProfitMargins(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.PROFIT_MARGINS, params);
    return response.data;
  }

  /**
   * Fetch delivery cost analysis data
   * @param {object} options - Request options
   * @returns {Promise} Delivery cost analysis data
   */
  static async getDeliveryCostAnalysis(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.DELIVERY_COST_ANALYSIS, params);
    return response.data;
  }

  /**
   * Fetch fee breakdown analytics data
   * @param {object} options - Request options
   * @returns {Promise} Fee breakdown data
   */
  static async getFeeBreakdown(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.FEE_BREAKDOWN, params);
    return response.data;
  }

  /**
   * Fetch merchant earnings with enhanced breakdown
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Merchant earnings data
   */
  static async getMerchantEarnings(merchantId, options = {}) {
    if (!merchantId) {
      throw new Error("Merchant ID is required");
    }

    const endpoint = ANALYTICS_ENDPOINTS.MERCHANT_EARNINGS.replace('{merchantId}', merchantId);
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(endpoint, params);
    return response.data;
  }

  /**
   * Fetch customers analytics data
   * @param {object} options - Request options
   * @returns {Promise} Customers data
   */
  static async getCustomers(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.CUSTOMERS, params);
    return response.data;
  }

  /**
   * Fetch disputes analytics data
   * @param {object} options - Request options
   * @returns {Promise} Disputes data
   */
  static async getDisputes(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.DISPUTES, params);
    return response.data;
  }

  /**
   * Fetch popular items analytics data
   * @param {object} options - Request options
   * @returns {Promise} Popular items data
   */
  static async getPopularItems(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.POPULAR_ITEMS, params);
    return response.data;
  }

  /**
   * Fetch trending items analytics data
   * @param {object} options - Request options
   * @returns {Promise} Trending items data
   */
  static async getTrendingItems(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.TRENDING_ITEMS, params);
    return response.data;
  }

  /**
   * Fetch item performance comparison data
   * @param {object} options - Request options
   * @returns {Promise} Item performance comparison data
   */
  static async getItemPerformanceComparison(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ITEM_PERFORMANCE_COMPARISON, params);
    return response.data;
  }

  /**
   * Fetch active sessions data
   * @param {object} options - Request options
   * @returns {Promise} Active sessions data
   */
  static async getActiveSessions(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ACTIVE_SESSIONS, params);
    return response.data;
  }

  /**
   * Fetch devices analytics data
   * @param {object} options - Request options
   * @returns {Promise} Devices data
   */
  static async getDevices(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.DEVICES, params);
    return response.data;
  }

  /**
   * Fetch stores analytics data
   * @param {object} options - Request options
   * @returns {Promise} Stores data
   */
  static async getStores(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.STORES, params);
    return response.data;
  }

  /**
   * Fetch merchants analytics data
   * @param {object} options - Request options
   * @returns {Promise} Merchants data
   */
  static async getMerchants(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.MERCHANTS, params);
    return response.data;
  }

  /**
   * Fetch staff analytics data
   * @param {object} options - Request options
   * @returns {Promise} Staff data
   */
  static async getStaff(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.STAFF, params);
    return response.data;
  }

  /**
   * Fetch managers analytics data
   * @param {object} options - Request options
   * @returns {Promise} Managers data
   */
  static async getManagers(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.MANAGERS, params);
    return response.data;
  }

  /**
   * Fetch admins analytics data
   * @param {object} options - Request options
   * @returns {Promise} Admins data
   */
  static async getAdmins(options = {}) {
    const params = buildAnalyticsParams(options);
    const response = await apiCallWithRetry(ANALYTICS_ENDPOINTS.ADMINS, params);
    return response.data;
  }

  /**
   * Batch fetch multiple analytics endpoints
   * @param {Array} endpoints - Array of endpoint configurations
   * @returns {Promise} Object with all fetched data
   */
  static async batchFetch(endpoints = []) {
    const promises = endpoints.map(async ({ endpoint, params = {}, options = {} }) => {
      try {
        const data = await this[endpoint](params);
        return { endpoint, data, success: true };
      } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);
        return { endpoint, error, success: false };
      }
    });

    const results = await Promise.allSettled(promises);
    
    return results.reduce((acc, result, index) => {
      const { endpoint } = endpoints[index];
      if (result.status === 'fulfilled') {
        acc[endpoint] = result.value;
      } else {
        acc[endpoint] = { error: result.reason, success: false };
      }
      return acc;
    }, {});
  }
}

export default AnalyticsAPI;
export { ANALYTICS_ENDPOINTS, RETRY_CONFIG };
